import Layout from '@/components/Layout';
import Banner from '@/components/Banner';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Tag, Bot, ChevronLeft, ChevronRight, List, X } from 'lucide-react';
import { Link, useParams, useLocation } from 'wouter';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { useGlobalCopy } from '@/hooks/use-global-copy';
import { useSEO, createArticleSchema } from '@/hooks/use-seo';
import { usePageLoading } from '@/hooks/use-page-loading';
import { UmamiPageViews } from '@/components/ui/umami-page-views';
import LicenseBox from '@/components/LicenseBox';
import SocialShare from '@/components/SocialShare';
import Twikoo from '@/components/Twikoo';
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

interface TocItem {
  id: string;
  text: string;
  level: number;
}
interface TocNode {
  id: string;
  text: string;
  parentId: string | null;
  children: { id: string; text: string }[];
}
interface PostPageProps {
  onSearchClick: () => void;
}
interface OffsetItem {
  id: string;
  top: number;
  parentId: string | null;
}

export default function PostSection({ onSearchClick }: PostPageProps) {
  const { id } = useParams();
  const [location, setLocation] = useLocation();
  const [post, setPost] = useState<any | null>(null);
  const [navigation, setNavigation] = useState<{ prev: any; next: any } | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [showTopBtn, setShowTopBtn] = useState(false);
  const headingOffsets = useRef<{ id: string; top: number; parentId: string | null }[]>([]);
  const isManualScrolling = useRef(false);
  const scrollEndTimeoutRef = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const savedScrollY = useRef(0);
  const updateOffsetsRef = useRef<() => void>(() => {});

  usePageLoading(dataLoaded);

  // 1. 数据获取
  useEffect(() => {
    if (!id) return;
    setDataLoaded(false);
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const [postRes, navRes] = await Promise.all([
          fetch(`/data/posts/${id}.json`, { signal: controller.signal }),
          fetch('/data/postNavigation.json', { signal: controller.signal })
        ]);
        if (postRes.ok) setPost(await postRes.json());
        if (navRes.ok) {
          const navData = await navRes.json();
          setNavigation(navData[id] || { prev: null, next: null });
        }
        setDataLoaded(true);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Fetch error:', err);
          setDataLoaded(true);
        }
      }
    };
    fetchData();
    return () => controller.abort();
  }, [id]);

  // 2. SEO
  const seoElement = useSEO({
    title: post?.title,
    description: post?.excerpt || post?.summary,
    keywords: post?.tags || [],
    ogType: 'article',
    structuredData: post ? createArticleSchema({
      title: post.title,
      description: post.excerpt || post.summary || '',
      image: post.cover,
      datePublished: post.date,
      author: import.meta.env.VITE_SITE_AUTHOR || '序窡',
      url: `${window.location.origin}/posts/${id}`,
      keywords: post.tags,
    }) : undefined,
  });

  // 3. 渐显动画
  useEffect(() => {
    if (post && !visible) requestAnimationFrame(() => setVisible(true));
  }, [post, visible]);

  // 4. TOC 数据
  const tocAndOffsets = useMemo(() => {
    if (!post?.toc) return { toc: [], offsets: [] };
    const nodes: TocNode[] = [];
    const offsets: any[] = [];
    let lastH2: TocNode | null = null;
    post.toc.forEach((item: TocItem) => {
      if (item.level === 2) {
        lastH2 = { id: item.id, text: item.text, parentId: null, children: [] };
        nodes.push(lastH2);
        offsets.push({ id: item.id, top: 0, parentId: null });
      } else if (item.level === 3 && lastH2) {
        lastH2.children.push({ id: item.id, text: item.text });
        offsets.push({ id: item.id, top: 0, parentId: lastH2.id });
      }
    });
    return { toc: nodes, offsets };
  }, [post]);

  const hasToc = tocAndOffsets.toc.length > 0;

  const updateOffsets = useCallback(() => {
    if (!contentRef.current?.isConnected || isManualScrolling.current) return;
    headingOffsets.current = tocAndOffsets.offsets.map((h) => {
      const el = document.getElementById(h.id);
      return el ? { ...h, top: el.getBoundingClientRect().top + window.pageYOffset } : h;
    });
  }, [tocAndOffsets]);

  updateOffsetsRef.current = updateOffsets;

  const debouncedUpdateOffsets = useCallback(() => {
    window.requestAnimationFrame(updateOffsets);
  }, [updateOffsets]);

  // 5. 文章内容生命周期
  useEffect(() => {
    const contentEl = contentRef.current;
    if (!post || !contentEl) return;
    const handleImgLoad = (e: Event) => {
      if ((e.target as HTMLElement).tagName === 'IMG') debouncedUpdateOffsets();
    };
    contentEl.addEventListener('load', handleImgLoad, true);

    // Footnote tooltip: hover on .footnote-ref a[data-footnote]
    let tooltip: HTMLDivElement | null = null;

    const showTooltip = (anchor: HTMLAnchorElement) => {
      const text = anchor.getAttribute('data-footnote');
      if (!text) return;
      tooltip = document.createElement('div');
      tooltip.className = 'footnote-tooltip';
      tooltip.textContent = text;
      document.body.appendChild(tooltip);
      const rect = anchor.getBoundingClientRect();
      const tipRect = tooltip.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - tipRect.width / 2;
      let top = rect.top - tipRect.height - 8;
      // Keep within viewport
      if (left < 8) left = 8;
      if (left + tipRect.width > window.innerWidth - 8) left = window.innerWidth - 8 - tipRect.width;
      if (top < 8) top = rect.bottom + 8; // flip below if no room above
      tooltip.style.left = left + 'px';
      tooltip.style.top = top + 'px';
    };

    const hideTooltip = () => {
      if (tooltip) { tooltip.remove(); tooltip = null; }
    };

    const onFootnoteEnter = (e: Event) => {
      const target = (e.target as HTMLElement).closest?.('.footnote-ref a[data-footnote]');
      if (target) showTooltip(target as HTMLAnchorElement);
    };
    const onFootnoteLeave = (e: Event) => {
      const target = (e.target as HTMLElement).closest?.('.footnote-ref a[data-footnote]');
      if (target) hideTooltip();
    };

    const onFootnoteClick = (e: Event) => { hideTooltip(); };

    contentEl.addEventListener('mouseover', onFootnoteEnter);
    contentEl.addEventListener('mouseout', onFootnoteLeave);
    contentEl.addEventListener('click', onFootnoteClick);

    const timer = setTimeout(debouncedUpdateOffsets, 500);
    return () => {
      clearTimeout(timer);
      hideTooltip();
      if (contentEl) {
        contentEl.removeEventListener('load', handleImgLoad, true);
        contentEl.removeEventListener('mouseover', onFootnoteEnter);
        contentEl.removeEventListener('mouseout', onFootnoteLeave);
        contentEl.removeEventListener('click', onFootnoteClick);
        contentEl.innerHTML = '';
      }
    };
  }, [id, post?.content, debouncedUpdateOffsets]);

  // 6. 滚动监听
  useEffect(() => {
    const onScroll = () => {
      if (!headingOffsets.current.length) return;
      const triggerPoint = window.scrollY + 100;
      let current: OffsetItem | null = null;
      for (const h of headingOffsets.current) {
        if (h.top <= triggerPoint) current = h;
        else break;
      }
      if (scrollEndTimeoutRef.current) {
        window.clearTimeout(scrollEndTimeoutRef.current);
        scrollEndTimeoutRef.current = null;
      }
      if (isManualScrolling.current) {
        if (current && current.id === activeId) {
          isManualScrolling.current = false;
        } else {
          scrollEndTimeoutRef.current = window.setTimeout(() => {
            scrollEndTimeoutRef.current = null;
            isManualScrolling.current = false;
            window.dispatchEvent(new Event('scroll'));
          }, 150);
          return;
        }
      }
      if (current && current.id !== activeId) {
        setActiveId(current.id);
        setExpandedId(current.parentId || current.id);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeId]);

  // 6.5 返回顶部按钮可见性
  useEffect(() => {
    const onScroll = () => {
      const shouldShow = window.scrollY > 300;
      if (shouldShow !== showTopBtn) setShowTopBtn(shouldShow);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [showTopBtn]);

  // 7. 目录点击
  const handleTocClick = (e: any, targetId: string, parentId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(targetId);
    if (!element) return;
    isManualScrolling.current = true;
    setActiveId(targetId);
    setExpandedId(parentId || targetId);
    const targetTop = element.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
    // 移动端点击目录项后不关闭抽屉
  };

  // 8. Lightbox 滚动锁定
  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      savedScrollY.current = scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.cssText = `position: fixed; width: 100%; top: -${scrollY}px; padding-right: ${scrollbarWidth}px;`;
    } else {
      document.documentElement.style.cssText = '';
      const restoreY = savedScrollY.current;
      savedScrollY.current = 0;
      if (restoreY > 0) window.scrollTo({ top: restoreY, behavior: 'instant' });
      updateOffsetsRef.current();
    }
    return () => { document.documentElement.style.cssText = ''; };
  }, [open]);

  useGlobalCopy();

  const images = useMemo(() => {
    if (!post?.content) return [];
    const doc = new DOMParser().parseFromString(post.content, 'text/html');
    return Array.from(doc.querySelectorAll('img')).map(img => ({ src: img.getAttribute('src') || '' }));
  }, [post?.content]);

  const handleContentClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const imgIndex = images.findIndex(img => img.src === target.getAttribute('src'));
      if (imgIndex !== -1) {
        setIndex(imgIndex);
        setOpen(true);
      }
      return;
    }
    const link = target.closest('a');
    if (link?.href && !link.href.includes(window.location.hostname)) {
      e.preventDefault();
      setLocation(`/redirect?url=${encodeURIComponent(link.href)}&from=${encodeURIComponent(location)}`);
    }
  };

  return (
    <Layout onSearchClick={onSearchClick} key={id}>
      {seoElement}
      <Banner title={post?.title ?? ''} height="standard" />
      <Lightbox open={open} close={() => setOpen(false)} index={index} slides={images} plugins={[Zoom]} noScroll={{ disabled: true }} />
      
      <div className={`transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}>
        {post && (
          <div className="grid py-12 px-4 grid-cols-1 xl:grid-cols-[1fr_768px_1fr] xl:gap-x-6">
            {/* 中间文章内容列 */}
            <div className="w-full max-w-[768px] mx-auto xl:mx-0 xl:col-start-2">
                    {/* 元信息 */}
                    <div className="mb-8 space-y-4">
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1"><Calendar className="h-4 w-4" /><span>{post.date.slice(0,10)}</span></div>
                        <div className="flex items-center gap-1"><Clock className="h-4 w-4" /><span>{post.readTime} 分钟</span></div>
                        <UmamiPageViews abbrlink={post?.abbrlink} />
                        <Link href={`/categories/${encodeURIComponent(post.category)}`}>
                          <Badge variant="secondary" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">{post.category}</Badge>
                        </Link>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <div className="flex flex-wrap gap-2">
                          {post.tags.map((tag: string) => (
                            <Link key={tag} href={`/tags/${encodeURIComponent(tag)}`}>
                              <Badge variant="outline" className="text-xs shrink-0 cursor-pointer transition-colors hover:bg-primary hover:text-primary-foreground">{tag}</Badge>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 文章摘要 */}
                    {post.summary && (
                      <div className="rounded-lg border border-border shadow-md bg-card p-4 mb-8 duration-300 hover:shadow-lg hover:border-primary/50">
                        <h3 className="text-base font-semibold mb-2 flex items-center gap-2"><Bot />文章摘要</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{post.summary}</p>
                      </div>
                    )}

                    {/* 文章主体 */}
                    <article className="prose prose-neutral dark:prose-invert max-w-none w-full overflow-x-hidden">
                      <div 
                        key={`content-${id}`} 
                        id="post-content" 
                        ref={contentRef} 
                        style={{ width: '100%', maxWidth: '100%', minWidth: '0', overflowX: 'hidden', boxSizing: 'border-box' }} 
                        dangerouslySetInnerHTML={{ __html: post.content }} 
                        onClick={handleContentClick} 
                      />
                    </article>

                    {/* License、分享、导航、评论 */}
                    <LicenseBox 
                      title={post.title} 
                      permalink={import.meta.env.VITE_SITE_URL + `/posts/${post.abbrlink}`} 
                      author={import.meta.env.VITE_SITE_AUTHOR} 
                      postedAt={post.date.slice(0,10)} 
                      updatedAt={post.update} 
                      license={import.meta.env.VITE_SITE_POSTS_LICENSE} 
                    />
                    <SocialShare 
                      title={post.title} 
                      url={import.meta.env.VITE_SITE_URL + `/posts/${post.abbrlink}`} 
                    />
                    
                    {/* 上下篇导航 */}
                    {navigation && (
                      <div className="mt-4 pt-4 mb-8">
                        <div className="flex flex-col sm:flex-row justify-between gap-6 text-sm">
                          <div className="flex-1 min-w-0">
                            {navigation.next ? (
                              <Link to={`/posts/${navigation.next.id}`} className="group block p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                                  <ChevronLeft className="h-4 w-4" />
                                  <span className="font-medium">下一篇</span>
                                </div>
                                <div className="line-clamp-2 font-medium group-hover:text-primary transition-colors">{navigation.next.title}</div>
                              </Link>
                            ) : (
                              <div className="p-4 rounded-lg border border-border bg-muted/30 text-muted-foreground">
                                <div className="flex items-center gap-2 mb-1">
                                  <ChevronLeft className="h-4 w-4" />
                                  <span className="font-medium">下一篇</span>
                                </div>
                                <div className="line-clamp-2 font-medium">已是最新文章</div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0 text-right sm:text-left">
                            {navigation.prev ? (
                              <Link to={`/posts/${navigation.prev.id}`} className="group block p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center justify-end sm:justify-start gap-2 text-muted-foreground mb-1">
                                  <span className="font-medium">上一篇</span>
                                  <ChevronRight className="h-4 w-4" />
                                </div>
                                <div className="line-clamp-2 font-medium group-hover:text-primary transition-colors">{navigation.prev.title}</div>
                              </Link>
                            ) : (
                              <div className="p-4 rounded-lg border border-border bg-muted/30 text-muted-foreground">
                                <div className="flex items-center justify-end sm:justify-start gap-2 mb-1">
                                  <span className="font-medium">上一篇</span>
                                  <ChevronRight className="h-4 w-4" />
                                </div>
                                <div className="line-clamp-2 font-medium">已是最旧文章</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div key={`twikoo-${id}`} className="mt-8">
                      <Twikoo envId={import.meta.env.VITE_TWIKOO_ENV} path={window.location.pathname} />
                    </div>
                  </div>

                  {/* 目录容器 (TOC) - 仅在大屏幕显示 */}
                  {hasToc && (
                    <aside className="hidden xl:block w-64">
                      <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto">
                      <Card className="rounded-lg border-border shadow-md overflow-hidden bg-card duration-300 hover:shadow-lg hover:border-primary/50">
                        <CardContent className="p-5">
                          <CardTitle className="text-sm font-bold mb-4">目录</CardTitle>
                          <nav>
                            <ul className="space-y-1">
                              {tocAndOffsets.toc.map((h2) => (
                                <li key={h2.id}>
                                  <a
                                    href={`#${h2.id}`}
                                    onClick={(e) => handleTocClick(e, h2.id, null)}
                                    className={`block py-1.5 text-sm transition-all border-l-2 pl-3 ${
                                      activeId === h2.id
                                        ? 'border-primary text-primary font-bold bg-primary/5'
                                        : 'border-transparent text-muted-foreground hover:text-foreground'
                                    }`}
                                  >
                                    {h2.text}
                                  </a>
                                  {expandedId === h2.id && h2.children.length > 0 && (
                                    <ul className="mt-1 mb-2 ml-4 space-y-1 border-l border-muted/20">
                                      {h2.children.map((h3) => (
                                        <li key={h3.id}>
                                          <a
                                            href={`#${h3.id}`}
                                            onClick={(e) => handleTocClick(e, h3.id, h2.id)}
                                            className={`block py-1 pl-4 text-xs transition-colors border-l-2 ${
                                              activeId === h3.id
                                                ? 'border-primary text-primary font-medium'
                                                : 'border-transparent text-muted-foreground'
                                            }`}
                                          >
                                            {h3.text}
                                          </a>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </nav>
                        </CardContent>
                    </Card>
                    </div>
                    </aside>
                  )}
          </div>
        )}
      </div>

      {/* 移动端 TOC 浮动按钮 */}
      {hasToc && (
        <button
          onClick={() => setMobileTocOpen(true)}
          className={`xl:hidden fixed right-6 z-50 rounded-lg bg-card text-foreground border border-border shadow-md p-3 transition-all duration-300 hover:shadow-lg hover:border-primary/50 active:scale-95 ${
            showTopBtn ? 'bottom-[5.25rem]' : 'bottom-6'
          }`}
          aria-label="打开目录"
        >
          <List className="h-6 w-6" />
        </button>
      )}

      {/* 移动端 TOC 抽屉 */}
      {hasToc && (
        <>
          {/* 背景遮罩 - 点击关闭 */}
          <div
            className={`xl:hidden fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
              mobileTocOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setMobileTocOpen(false)}
          />
          {/* 抽屉面板 */}
          <div
            className={`xl:hidden fixed top-0 right-0 z-[70] h-full w-72 max-w-[85vw] bg-card border-l border-border shadow-2xl transition-transform duration-300 ease-out ${
              mobileTocOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-sm font-bold">目录</h3>
              <button
                onClick={() => setMobileTocOpen(false)}
                className="rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="关闭目录"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 57px)' }}>
              <ul className="space-y-1">
                {tocAndOffsets.toc.map((h2) => (
                  <li key={h2.id}>
                    <a
                      href={`#${h2.id}`}
                      onClick={(e) => handleTocClick(e, h2.id, null)}
                      className={`block py-1.5 text-sm transition-all border-l-2 pl-3 ${
                        activeId === h2.id
                          ? 'border-primary text-primary font-bold bg-primary/5'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {h2.text}
                    </a>
                    {expandedId === h2.id && h2.children.length > 0 && (
                      <ul className="mt-1 mb-2 ml-4 space-y-1 border-l border-muted/20">
                        {h2.children.map((h3) => (
                          <li key={h3.id}>
                            <a
                              href={`#${h3.id}`}
                              onClick={(e) => handleTocClick(e, h3.id, h2.id)}
                              className={`block py-1 pl-4 text-xs transition-colors border-l-2 ${
                                activeId === h3.id
                                  ? 'border-primary text-primary font-medium'
                                  : 'border-transparent text-muted-foreground'
                              }`}
                            >
                              {h3.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </>
      )}
    </Layout>
  );
}